import { z } from 'zod';
import { BillingIntervalEnum } from '../types/subscriptions/enums';
import { IsoTimestampSchema, Sha256HexSchema } from './primitives';
/** ISO weekdays: Monday = 1 through Sunday = 7. No browser locale mapping. */
export const BillingWeekdaySchema = z.number().int().min(1).max(7);
export const CalendarBillingRuleSchema = z.discriminatedUnion('frequency', [
    z
        .object({ frequency: z.literal('WEEKLY'), weekday: BillingWeekdaySchema })
        .strict(),
    z
        .object({
        frequency: z.literal('MONTHLY'),
        dayOfMonth: z.number().int().min(1).max(28),
    })
        .strict(),
    z
        .object({
        frequency: z.literal('YEARLY'),
        month: z.number().int().min(1).max(12),
        dayOfMonth: z.number().int().min(1).max(28),
    })
        .strict(),
]);
export const InitialChargeModeSchema = z.enum([
    'IMMEDIATE',
    'NEXT_SCHEDULED_DATE',
]);
/** Optional on historical offers; never default this field into stored requests. */
export const BillingPolicySchema = z.discriminatedUnion('kind', [
    z
        .object({
        schemaVersion: z.literal(1),
        kind: z.literal('SIGNUP_ANNIVERSARY'),
        initialChargeMode: z.literal('IMMEDIATE'),
    })
        .strict(),
    z
        .object({
        schemaVersion: z.literal(1),
        kind: z.literal('CALENDAR'),
        initialChargeMode: InitialChargeModeSchema,
        rule: CalendarBillingRuleSchema,
    })
        .strict(),
]);
export const BillingTimeZoneSchema = z
    .string()
    .min(1)
    .max(100)
    .refine((value) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}, 'Use a valid IANA business timezone');
export function billingPolicyMatchesInterval(policy, interval, intervalCount) {
    return (!policy ||
        (intervalCount === 1 &&
            [
                BillingIntervalEnum.WEEKLY,
                BillingIntervalEnum.MONTHLY,
                BillingIntervalEnum.YEARLY,
            ].some((value) => value === interval) &&
            (policy.kind === 'SIGNUP_ANNIVERSARY' ||
                policy.rule.frequency === interval)));
}
/** Server-resolved, customer-visible terms. Revision binds review to dates and money;
 * it contains no provider identifier or credential. */
export const BillingSchedulePreviewSchema = z
    .object({
    schemaVersion: z.literal(1),
    policy: BillingPolicySchema,
    timeZone: BillingTimeZoneSchema,
    amountCents: z.number().int().positive().safe(),
    currency: z.literal('USD'),
    initialAmountCents: z.number().int().nonnegative().safe(),
    interval: z.enum(['WEEKLY', 'MONTHLY', 'YEARLY']),
    intervalCount: z.literal(1),
    collectionDates: z.tuple([
        IsoTimestampSchema,
        IsoTimestampSchema,
        IsoTimestampSchema,
    ]),
    revision: Sha256HexSchema,
    reviewExpiresAt: IsoTimestampSchema,
})
    .strict()
    .superRefine((value, ctx) => {
    if (!billingPolicyMatchesInterval(value.policy, value.interval, value.intervalCount))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['policy'],
            message: 'Schedule frequency must match the offer',
        });
    const expectedInitial = value.policy.initialChargeMode === 'IMMEDIATE' ? value.amountCents : 0;
    if (value.initialAmountCents !== expectedInitial)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['initialAmountCents'],
            message: 'Initial amount must match the charge policy',
        });
    if (value.collectionDates.some((date, index, dates) => index > 0 && Date.parse(date) <= Date.parse(dates[index - 1])))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['collectionDates'],
            message: 'Collection dates must increase',
        });
});
/** Frozen before execution. A retry uses this strategy and expiry verbatim. */
export const AcceptedBillingScheduleSchema = z
    .object({
    schemaVersion: z.literal(1),
    preview: BillingSchedulePreviewSchema,
    acceptedAt: IsoTimestampSchema,
    sessionExpiresAt: IsoTimestampSchema,
    providerStrategy: z.enum([
        'ANNIVERSARY',
        'DEFERRED_ANCHOR',
        'DEFERRED_TRIAL',
        'IMMEDIATE_ALIGN',
    ]),
})
    .strict()
    .superRefine((value, ctx) => {
    const { policy } = value.preview;
    const validStrategy = policy.kind === 'SIGNUP_ANNIVERSARY'
        ? value.providerStrategy === 'ANNIVERSARY'
        : policy.initialChargeMode === 'IMMEDIATE'
            ? value.providerStrategy === 'IMMEDIATE_ALIGN'
            : ['DEFERRED_ANCHOR', 'DEFERRED_TRIAL'].includes(value.providerStrategy);
    if (!validStrategy)
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['providerStrategy'],
            message: 'Provider strategy must implement the accepted policy',
        });
    if (Date.parse(value.sessionExpiresAt) <= Date.parse(value.acceptedAt) ||
        (policy.kind === 'CALENDAR' &&
            Date.parse(value.sessionExpiresAt) >=
                Date.parse(value.preview.collectionDates[0])))
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['sessionExpiresAt'],
            message: 'Checkout must expire before the first calendar collection',
        });
});
/** Backend evidence only; never a paid sale or a guarantee of future collection. */
export const VerifiedBillingSetupSchema = z
    .object({
    schemaVersion: z.literal(1),
    outcome: z.literal('BILLING_SCHEDULED'),
    verifiedAt: IsoTimestampSchema,
    scheduleRevision: Sha256HexSchema,
    firstCollectionAt: IsoTimestampSchema,
    paymentMethodId: z.string().regex(/^pm_[A-Za-z0-9]+$/),
    setupIntentId: z
        .string()
        .regex(/^seti_[A-Za-z0-9]+$/)
        .optional(),
})
    .strict();
export const BillingScheduleOperationSchema = z
    .object({
    schemaVersion: z.literal(1),
    state: z.enum(['PENDING', 'APPLIED', 'CANCELED']),
    subscriptionId: z.string().regex(/^sub_[A-Za-z0-9]+$/),
    idempotencyKey: z.string().min(1).max(255),
    firstCollectionAt: IsoTimestampSchema,
    preparedAt: IsoTimestampSchema,
    appliedAt: IsoTimestampSchema.optional(),
})
    .strict();
/** Read-only authoring preview input. The server supplies business timezone. */
export const PreviewBillingScheduleRequestSchema = z
    .object({
    amountCents: z.number().int().positive().safe(),
    currency: z.literal('USD'),
    interval: z.enum(['WEEKLY', 'MONTHLY', 'YEARLY']),
    intervalCount: z.literal(1),
    billingPolicy: BillingPolicySchema,
})
    .strict()
    .refine((value) => billingPolicyMatchesInterval(value.billingPolicy, value.interval, value.intervalCount), {
    path: ['billingPolicy'],
    message: 'Schedule frequency must match the offer',
});
export function describeBillingPolicy(policy) {
    if (policy.kind === 'SIGNUP_ANNIVERSARY')
        return 'on the signup anniversary';
    const rule = policy.rule;
    if (rule.frequency === 'WEEKLY')
        return `each ${['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'][rule.weekday - 1]}`;
    if (rule.frequency === 'MONTHLY')
        return `on day ${rule.dayOfMonth} of each month`;
    return `each year on ${['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][rule.month - 1]} ${rule.dayOfMonth}`;
}
